/**
 * 图片节点：展示生成结果或外链 / 上传的图片。
 *
 * 工具栏：
 *  - 作为参考图（创建一个 generate_image 节点并连线，mode=img2img）
 *  - 局部重绘（创建一个 generate_image 节点并连线，mode=inpaint）
 *  - 下载图片
 */
import { Download, Brush, ImagePlus, AlertTriangle, Loader2, Crop, Bookmark, RotateCcw, RotateCw, FlipHorizontal, Lock, Unlock, ChevronDown, Layers, Grid3x3, ScanSearch, Upload, MoreHorizontal, Palette, Maximize2 } from 'lucide-react';
import { useState, useCallback, useRef, useEffect } from 'react';
import type { CanvasNode, GenerateImageNodeData, ImageNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton, NodeToolbarDivider } from './NodeToolbar';
import { useMenuExternalDismiss } from '../hooks/useMenuExternalDismiss';
import { boardActions, getBoardState } from '../state/boardStore';
import { makeNode, nid } from '../utils';
import { isGeneratingStatus } from '../utils/jobStatus';
import { CropDialog } from '../CropDialog';
import { AngleDialog } from '../AngleDialog';
import { SplitDialog } from '../SplitDialog';
import { AssetPickerModal } from '../AssetPickerModal';
import { ImageLightbox } from '../components/ImageLightbox';
import { assetStorage } from '../assetStorage';
import { rotateImage, flipImage } from '../../../utils/imageTransform';
import { blobUrlToDataUrl, resolveImageUrl, uploadImage, bindUploadToNode } from '../imageStorage';
import { MAX_FALLBACK_DATA_URL_LENGTH } from '../constants';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { StyleSubmitDialog } from './StyleSubmitDialog';
import { TextEditorDialog } from '../components/dialogs/TextEditorDialog';
import { runLlmGeneration } from '../generators/llmGenerator';
import { replaceImageNodeWithFiles } from '../utils/nodeFileReplace';
import toast from '../../../components/Toast';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { deriveThumbnailUrl } from '../../../utils/imageDisplay';
import { parseAspectRatioValue } from '../utils/aspectRatio';

interface ImageNodeProps {
  node: CanvasNode<ImageNodeData>;
  selected: boolean;
  highlighted: boolean;
}

export function ImageNode({ node, selected, highlighted }: ImageNodeProps) {
  const data = node.data;
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const url = resolvedUrl || data.imageUrl ? resolveBrowserMediaUrl(resolvedUrl || data.imageUrl || '') || null : null;
  const [imageInfo, setImageInfo] = useState<{ width: number; height: number } | null>(null);
  // 卡片展示优先用 medium 缩略图（最大边 1024，省流量）；卡片被拉大到 512 以上
  //（高 dpr 下缩略图会糊）时切回原图，同时保证尺寸角标反映真实像素
  const preferOriginal = node.width >= 512;
  const thumbnailUrl = data.imageUrl ? deriveThumbnailUrl(data.imageUrl, 'medium') : null;
  const displayUrl: string | undefined =
    url && !preferOriginal && thumbnailUrl ? resolveBrowserMediaUrl(thumbnailUrl) : url ?? undefined;
  const [cropOpen, setCropOpen] = useState(false);
  const [angleOpen, setAngleOpen] = useState(false);
  const [splitOpen, setSplitOpen] = useState(false);
  const [reverseBusy, setReverseBusy] = useState(false);
  const [transforming, setTransforming] = useState(false);
  const [toolbarMenu, setToolbarMenu] = useState<'transform' | 'ai' | 'more' | null>(null);
  const [assetPickerOpen, setAssetPickerOpen] = useState(false);
  const [styleSubmitOpen, setStyleSubmitOpen] = useState(false);
  const [promptReviewOpen, setPromptReviewOpen] = useState(false);
  // 全屏大图预览（复用图片组同款灯箱；预览一律加载原图，保证清晰度）
  const [previewOpen, setPreviewOpen] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);
  const toolbarMenuRef = useRef<HTMLDivElement | null>(null);

  // 文件选择：替换本节点内容（多文件时其余新建节点）
  const openFilePicker = () => replaceInputRef.current?.click();
  const onFileChosen = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length > 0) void replaceImageNodeWithFiles(node, files);
  };

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth && img.naturalHeight) {
      setImageInfo({ width: img.naturalWidth, height: img.naturalHeight });
    }
  }, []);

  // 点击外部 / Escape / 画布缩放时关闭工具栏下拉（见 useMenuExternalDismiss 注释）
  useMenuExternalDismiss(toolbarMenuRef, () => setToolbarMenu(null), toolbarMenu !== null);

  // 刷新后 blob URL 失效时，从 IndexedDB 重新解析
  useEffect(() => {
    // 只读预览：IndexedDB 里没有别人账号的图，跳过恢复
    if (getBoardState().readOnly) {
      setResolvedUrl(null);
      return;
    }
    const key = data._imageStorageKey;
    const currentUrl = data.imageUrl;
    if (!key || !currentUrl || !currentUrl.startsWith('blob:')) {
      setResolvedUrl(null);
      return;
    }
    // 尝试加载图片，如果失败则从 IndexedDB 重新解析
    const testImg = new Image();
    testImg.onload = () => setResolvedUrl(null); // blob URL 仍然有效
    testImg.onerror = async () => {
      try {
        const resolved = await resolveImageUrl(key);
        if (resolved) {
          // 会话内派生的 blob URL，静默写入避免刷新后触发幽灵保存
          boardActions.updateNodeDataSilent(node.id, { imageUrl: resolved });
          setResolvedUrl(resolved);
        }
      } catch {
        // resolve 失败，保持原 url
      }
    };
    testImg.src = currentUrl;
  }, [data._imageStorageKey, data.imageUrl, node.id]);

  const handleTransform = async (action: () => Promise<string>) => {
    if (!url || transforming) return;
    setTransforming(true);
    try {
      const newDataUrl = await action();
      // 派生图远程上传（跟账号走）：新节点先渲染本地预览，上传完成后换持久网关地址
      const blob = await (await fetch(newDataUrl)).blob();
      const up = uploadImage(blob, getBoardState().projectId ?? undefined);
      const newNode = makeNode('image', {
        x: node.x + node.width + 60,
        y: node.y,
      }, {
        imageUrl: up.previewUrl,
        status: 'completed',
        prompt: data.prompt ? `(变换) ${data.prompt}` : '(变换)',
      } as Partial<ImageNodeData> as ImageNodeData);
      boardActions.addNode(newNode);
      bindUploadToNode(newNode.id, up);
      boardActions.addEdge({
        id: nid(),
        sourceNodeId: node.id,
        targetNodeId: newNode.id,
        sourceHandle: 'right',
        targetHandle: 'left',
      });
    } catch (err) {
      console.error('[imageTransform] 变换失败', err);
    } finally {
      setTransforming(false);
      setToolbarMenu(null);
    }
  };

  const createIterateNode = (mode: 'img2img' | 'inpaint') => {
    const x = node.x + node.width + 60;
    const y = node.y;
    const newNode = makeNode<GenerateImageNodeData>(
      'generate_image',
      { x, y },
      { mode, aspectRatio: 'auto', quality: 'low', count: 1 },
      undefined,
      node.zIndex
    );
    boardActions.addNode(newNode);
    boardActions.addEdge({
      id: nid(),
      sourceNodeId: node.id,
      targetNodeId: newNode.id,
      sourceHandle: 'right',
      targetHandle: 'left',
    });
  };

  const handleDownload = async () => {
    if (!url) return;
    try {
      const resp = await fetch(url);
      const blob = await resp.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `canvas-image-${node.id.slice(0, 8)}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objUrl);
    } catch {
      window.open(url, '_blank');
    }
  };

  // 反推提示词：vision 模式流式图生文，结果写入新建文本节点并自动搭好生图链路
  const handleReversePrompt = async () => {
    if (!url || reverseBusy) return;
    setReverseBusy(true);
    setToolbarMenu(null);
    try {
      // 召唤 LLM 反推节点：image → llm 连线后 LLM 检测到上游图片即强制反推模式
      // （上游有图 = 只能反推，无需输入提示词），输出写入 LLM 节点可直接被下游消费
      const llmNode = makeNode(
        'llm',
        { x: node.x + node.width + 60, y: node.y },
        { input: '' } as Record<string, unknown>
      );
      boardActions.addNodes([llmNode], [
        {
          id: nid(),
          sourceNodeId: node.id,
          targetNodeId: llmNode.id,
          sourceHandle: 'right',
          targetHandle: 'left',
        },
      ]);
      await runLlmGeneration(llmNode.id);
      toast.success('反推完成，已生成 LLM 反推节点');
    } catch (err) {
      // 节点已建：反推失败时保留 LLM 节点，用户可重试或删除
      toast.error(err instanceof Error ? err.message : '反推提示词失败');
    } finally {
      setReverseBusy(false);
    }
  };

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      toolbar={
        url && data.status === 'completed' ? (
          <>
            {/* 变换下拉：裁剪 / 切割 / 旋转 / 翻转 */}
            <div className="relative" ref={toolbarMenu === 'transform' ? toolbarMenuRef : undefined}>
                <NodeToolbarButton
                  onClick={() => setToolbarMenu(toolbarMenu === 'transform' ? null : 'transform')}
                  active={toolbarMenu === 'transform'}
                  title="变换（裁剪、切割、旋转、翻转）"
                >
                  <Layers className="h-3 w-3" />
                  变换
                  <ChevronDown className="h-2.5 w-2.5" />
                </NodeToolbarButton>
              {toolbarMenu === 'transform' && (
                <div className="absolute left-0 top-full z-50 mt-1 min-w-[110px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  <button type="button" onClick={() => { setCropOpen(true); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <Crop className="h-3 w-3 text-emerald-500" /> 裁剪
                  </button>
                  <button type="button" onClick={() => { setSplitOpen(true); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <Grid3x3 className="h-3 w-3 text-teal-500" /> 切割
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  {([90, 180, 270] as const).map((deg) => (
                    <button key={deg} type="button" onClick={() => handleTransform(() => rotateImage(url!, deg))}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                      <RotateCw className="h-3 w-3 text-cyan-500" /> 旋转 {deg}°
                    </button>
                  ))}
                  <div className="my-1 border-t border-slate-100" />
                  <button type="button" onClick={() => handleTransform(() => flipImage(url!, 'horizontal'))}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <FlipHorizontal className="h-3 w-3 text-violet-500" /> 水平翻转
                  </button>
                  <button type="button" onClick={() => handleTransform(() => flipImage(url!, 'vertical'))}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <FlipHorizontal className="h-3 w-3 -scale-y-100 text-violet-500" /> 垂直翻转
                  </button>
                </div>
              )}
            </div>

            {/* AI 下拉：参考 / 重绘 / 多角度 */}
            <div className="relative" ref={toolbarMenu === 'ai' ? toolbarMenuRef : undefined}>
                <NodeToolbarButton
                  onClick={() => setToolbarMenu(toolbarMenu === 'ai' ? null : 'ai')}
                  active={toolbarMenu === 'ai'}
                  tone="primary"
                  title="AI 操作（参考图、局部重绘、反推提示词、多角度）"
                >
                  <ImagePlus className="h-3 w-3" />
                  AI
                  <ChevronDown className="h-2.5 w-2.5" />
                </NodeToolbarButton>
              {toolbarMenu === 'ai' && (
                <div className="absolute left-0 top-full z-50 mt-1 min-w-[110px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  <button type="button" onClick={() => { createIterateNode('img2img'); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <ImagePlus className="h-3 w-3 text-blue-500" /> 作为参考图
                  </button>
                  <button type="button" onClick={() => { createIterateNode('inpaint'); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <Brush className="h-3 w-3 text-orange-500" /> 局部重绘
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button type="button" onClick={() => { void handleReversePrompt(); }}
                    disabled={reverseBusy}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-60">
                    {reverseBusy ? <Loader2 className="h-3 w-3 animate-spin text-indigo-500" /> : <ScanSearch className="h-3 w-3 text-indigo-500" />}
                    {reverseBusy ? '反推中...' : '反推提示词'}
                  </button>
                  <button type="button" onClick={() => { setAngleOpen(true); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50">
                    <RotateCcw className="h-3 w-3 text-indigo-500" /> AI 多角度
                  </button>
                </div>
              )}
            </div>

            <NodeToolbarDivider />
            <div className="relative" ref={toolbarMenu === 'more' ? toolbarMenuRef : undefined}>
              <NodeToolbarButton
                onClick={() => setToolbarMenu(toolbarMenu === 'more' ? null : 'more')}
                active={toolbarMenu === 'more'}
                title="更多图片操作"
                aria-label="更多图片操作"
              >
                <MoreHorizontal className="h-3.5 w-3.5" />
              </NodeToolbarButton>
              {toolbarMenu === 'more' && (
                <div className="absolute right-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
                  <button
                    type="button"
                    onClick={() => { openFilePicker(); setToolbarMenu(null); }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Upload className="h-3 w-3 text-slate-500" /> 替换图片
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const cur = data.lockRatio ?? true;
                      boardActions.updateNodeData(node.id, { lockRatio: !cur });
                      setToolbarMenu(null);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                  >
                    {(data.lockRatio ?? true) ? <Lock className="h-3 w-3 text-blue-500" /> : <Unlock className="h-3 w-3 text-slate-400" />}
                    {(data.lockRatio ?? true) ? '解锁比例' : '锁定比例'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!url) return;
                      void (async () => {
                        let content = toPersistableMediaUrl(url) ?? url;
                        if (content.startsWith('blob:')) {
                          const dataUrl = await blobUrlToDataUrl(content);
                          if (!dataUrl) {
                            toast.error('收藏失败：本地图片已失效，请重新上传后再收藏');
                            return;
                          }
                          if (dataUrl.length > MAX_FALLBACK_DATA_URL_LENGTH) {
                            toast.error('图片过大，无法收藏到素材库');
                            return;
                          }
                          content = dataUrl;
                        }
                        const result = assetStorage.add({
                          type: 'image',
                          content,
                          prompt: data.prompt ?? undefined,
                          projectId: getBoardState().projectId,
                        });
                        toast.success(result.created ? '已收藏到素材库' : '该素材已在素材库中');
                        setToolbarMenu(null);
                      })();
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Bookmark className="h-3 w-3 text-amber-500" /> 收藏到素材库
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setStyleSubmitOpen(true);
                      setToolbarMenu(null);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-slate-50"
                  >
                    <Palette className="h-3 w-3 text-fuchsia-500" /> 存为风格卡片
                  </button>
                </div>
              )}
            </div>
            <NodeToolbarButton onClick={handleDownload} title="下载图片">
              <Download className="h-3.5 w-3.5" />
            </NodeToolbarButton>
          </>
        ) : null
      }
    >
      <div
        className="relative flex w-full items-center justify-center overflow-hidden rounded-xl bg-slate-100"
        // 所有状态统一按比例绘制：有图用真实宽高比；未加载完 / 无媒体（空节点、
        // 生成中、失败）用上游 generate_image 的目标比例（'auto' 或缺省按 1:1），
        // 空态、生成态、完成态的占位尺寸完全一致，卡片高度由 ResizeObserver 吸附
        style={{
          aspectRatio: imageInfo
            ? imageInfo.width / imageInfo.height
            : parseAspectRatioValue(data.aspectRatio, 1),
        }}
      >
        {/* 图号角标：@图N 引用的稳定编号，常驻左上角 */}
        {typeof data.refNo === 'number' && (
          <span
            className="pointer-events-none absolute left-2 top-2 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
            title={`图号 #${data.refNo}：提示词中可用 @图${data.refNo} 引用本图`}
          >
            #{data.refNo}
          </span>
        )}
        {/* 非终态一律视为生成中（兼容历史快照中的 submitting 等未知中间态），防止掉进空节点分支 */}
        {isGeneratingStatus(data.status) ? (
          url ? (
            /* 两阶段渲染：有预览图时显示图片 + 轻量 loading 指示 */
            <div className="relative h-full w-full">
              <img src={displayUrl} alt={data.prompt || ''} className="h-full w-full object-contain opacity-70" onLoad={onImageLoad} />
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-white">
                处理中...
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center text-light-muted">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="mt-2 text-xs">生成中...</span>
            </div>
          )
        ) : data.status === 'failed' ? (
          <div className="flex flex-col items-center px-3 py-4 text-center text-red-500">
            <AlertTriangle className="h-6 w-6" />
            <span className="mt-2 text-xs font-medium">生成失败</span>
            {data.errorMessage && (
              <span className="mt-1 line-clamp-2 text-[11px] text-red-400/80">{data.errorMessage}</span>
            )}
          </div>
        ) : url ? (
          <>
            <img
              src={displayUrl}
              alt={data.prompt || ''}
              className="h-full w-full cursor-zoom-in object-contain"
              loading="lazy"
              decoding="async"
              onLoad={onImageLoad}
              onClick={() => setPreviewOpen(true)}
              title="点击放大预览"
            />
            {/* 尺寸角标：黑底白字，悬停显示（对齐 infinite-canvas ImageInfoBar） */}
            {imageInfo && (
              <span
                className="pointer-events-none absolute bottom-2 right-2 max-w-[calc(100%-16px)] truncate rounded-md bg-black/55 px-2 py-1 text-[11px] font-medium leading-none text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100"
                title={
                  preferOriginal
                    ? '图片原始像素尺寸'
                    : `缩略图预览尺寸（卡片拉大到 512 以上会切换原图并显示原始尺寸）`
                }
              >
                {imageInfo.width} x {imageInfo.height}
              </span>
            )}
          </>
        ) : (
          /* 空图片节点：本地上传 / 从图片库选择（保留节点位置与连线） */
          <div className="flex h-full w-full flex-col items-stretch justify-center gap-1.5 px-2 text-light-muted">
            <button
              type="button"
              onClick={openFilePicker}
              className="flex flex-1 flex-col items-center justify-center gap-1 rounded-lg transition hover:bg-slate-100 hover:text-slate-500"
            >
              <ImagePlus className="h-5 w-5" />
              <span className="text-xs">空图片节点 · 点击上传</span>
            </button>
            <button
              type="button"
              onClick={() => setAssetPickerOpen(true)}
              className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-slate-200 py-1.5 text-xs text-slate-500 transition hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600"
            >
              <Layers className="h-3.5 w-3.5" />
              从图片库选择
            </button>
          </div>
        )}
      </div>
      {data.modelLabel && (
        <p className="mt-2 flex items-center gap-1 text-[10px] text-slate-400">
          <span className="truncate">{data.modelLabel}</span>
        </p>
      )}
      {data.prompt && (
        <div className="mt-1 flex items-start gap-1">
          <p
            className="line-clamp-2 cursor-pointer text-xs text-light-muted transition hover:text-slate-600"
            onClick={() => setPromptReviewOpen(true)}
            title="点击放大查看"
          >
            {data.prompt}
          </p>
          <button
            type="button"
            onClick={() => setPromptReviewOpen(true)}
            className="mt-0.5 shrink-0 rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600"
            title="放大查看"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>
      )}
      {cropOpen && url && (
        <CropDialog
          imageUrl={url}
          sourceNode={node}
          onClose={() => setCropOpen(false)}
        />
      )}
      {assetPickerOpen && (
        <AssetPickerModal
          open={assetPickerOpen}
          assetFilter="image"
          onClose={() => setAssetPickerOpen(false)}
          onPick={(asset) => {
            // 回填本节点（保留位置与连线）；清洗收藏时间隔的旧令牌，快照不落过期凭据
            boardActions.updateNodeData(node.id, {
              imageUrl: toPersistableMediaUrl(asset.content) ?? asset.content,
              status: 'completed',
              prompt: asset.prompt ?? data.prompt,
            });
          }}
        />
      )}
      {splitOpen && url && (
        <SplitDialog
          sourceNode={node}
          imageUrl={url}
          onClose={() => setSplitOpen(false)}
        />
      )}
      {angleOpen && (
        <AngleDialog
          sourceNode={node}
          onClose={() => setAngleOpen(false)}
        />
      )}
      {/* 全屏大图预览（单图；预览用原图 url，不受卡片缩略图策略影响） */}
      {previewOpen && url && (
        <ImageLightbox
          images={[{ url, label: data.prompt ? data.prompt.slice(0, 40) : undefined }]}
          index={0}
          onIndexChange={() => undefined}
          onClose={() => setPreviewOpen(false)}
          onDownload={() => {
            void handleDownload();
          }}
        />
      )}
      {/* 替换图片的隐藏文件选择器 */}
      <input
        ref={replaceInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={onFileChosen}
      />

      {/* 存为风格卡片弹窗 */}
      {styleSubmitOpen && url && (
        <StyleSubmitDialog
          imageUrl={toPersistableMediaUrl(url) ?? url}
          defaultPrompt={data.prompt ?? ''}
          onClose={() => setStyleSubmitOpen(false)}
        />
      )}
      {/* 提示词放大审阅弹窗（只读） */}
      {promptReviewOpen && data.prompt && (
        <TextEditorDialog
          title="提示词"
          initialValue={data.prompt}
          readOnly
          onClose={() => setPromptReviewOpen(false)}
        />
      )}
    </NodeWrapper>
  );
}
